import dayjs from "dayjs";
import React, { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  Space,
  Badge,
  Typography,
  Popover,
  List,
  Grid,
  Drawer,
} from "antd";
import {
  DashboardOutlined,
  CalendarOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  CoffeeOutlined,
  CompassOutlined,
  AuditOutlined,
  PrinterOutlined,
  AccountBookOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { socketService } from "../../services/socketService";
import { getStoredUser } from "../../utils/permissions";
import "./AdminLayout.css";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Display fallback when no cached user is available (central reader in
// utils/permissions returns null in that case).
const getDisplayUser = () => {
  const user = getStoredUser();
  if (!user || !user.name) return { name: "Admin", role: "staff", permissions: [] as string[] };
  return { ...user, permissions: user.permissions || [] };
};

// Full menu definition — each item tagged with the permission required to see it
const ALL_MENU_ITEMS = [
  {
    key: "/dashboard",
    icon: <DashboardOutlined />,
    label: "Dashboard",
    permission: "dashboard:view",
  },
  {
    key: "/bookings",
    icon: <CalendarOutlined />,
    label: "Website Bookings",
    permission: "bookings:view",
  },
  {
    key: "/front-desk",
    icon: <CalendarOutlined />,
    label: "Front Desk",
    permission: "bookings:edit",
  },
  {
    key: "/rooms",
    icon: <HomeOutlined />,
    label: "Room Management",
    permission: "rooms:view",
    children: [
      { key: "/rooms/list", label: "All Rooms" },
      { key: "/rooms/types", label: "Room Types" },
      { key: "/rooms/availability", label: "Availability" },
      { key: "/rooms/workflows", label: "Workflows" },
      { key: "/room-packages", label: "Room Packages" },
    ],
  },
  {
    key: "/guests",
    icon: <UserOutlined />,
    label: "Guest Management",
    permission: "guests:view",
  },
  {
    key: "/restaurant-management",
    icon: <CoffeeOutlined />,
    label: "Restaurant Management",
    permission: "restaurant:view",
    children: [
      { key: "/services/restaurant-pos", label: "Restaurant POS" },
      {
        key: "/services/restaurant-reservations",
        label: "Restaurant Reservations",
      },
    ],
  },
  {
    key: "/tour-management",
    icon: <CompassOutlined />,
    label: "Tour Management",
    permission: "restaurant:view",
    children: [
      { key: "/services/sports-activities", label: "Adam's Tour Reservations", permission: "activities:view" },
      { key: "/services/sports-operations", label: "Adam's Tour Operations", permission: "activities:view" },
    ],
  },
  {
    key: "/services",
    icon: <ShoppingOutlined />,
    label: "Services",
    permission: "restaurant:view",
    children: [
      { key: "/services/contact-submissions", label: "Contact Submissions" },
      { key: "/services/website-content", label: "Website Content" },
      { key: "/services/newsletter", label: "Newsletter" },
    ],
  },
  {
    key: "/kitchen",
    icon: <CoffeeOutlined />,
    label: "Kitchen",
    permission: "kitchen:view",
    children: [
      { key: "/services/restaurant-pos?tab=kot", label: "KOT Queue", permission: "restaurant:view" },
    ],
  },
  {
    key: "/housekeeping",
    icon: <HomeOutlined />,
    label: "Housekeeping",
    permission: "housekeeping:view",
    children: [
      { key: "/rooms/workflows", label: "Room Workflows", permission: "rooms:view" },
      { key: "/services/laundry", label: "Laundry Service", permission: "laundry:view" },
    ],
  },
  {
    key: "/billing",
    icon: <BarChartOutlined />,
    label: "Billing Engine",
    permission: "billing:view",
  },
  {
    key: "/calendar/advanced",
    icon: <CalendarOutlined />,
    label: "Reservation Calendar",
    permission: "bookings:view",
  },
  {
    key: "/notifications",
    icon: <BellOutlined />,
    label: "Notifications",
    permission: "notifications:view",
  },
  {
    key: "/staff",
    icon: <TeamOutlined />,
    label: "Staff Management",
    permission: "staff:view",
  },
  {
    key: "/finance-group",
    icon: <AccountBookOutlined />,
    label: "Income & Expense",
    permission: "finance:view",
    children: [
      { key: "/finance", label: "Finance Dashboard" },
      { key: "/finance/vouchers?type=income", label: "Income" },
      { key: "/finance/vouchers?type=expense", label: "Expenses" },
      { key: "/finance/approvals", label: "Approvals", permission: "finance:approve" },
      { key: "/finance/purchases", label: "Purchase Invoices" },
      { key: "/finance/inventory", label: "Inventory", permission: "inventory:view" },
      { key: "/finance/payroll", label: "Payroll", permission: "payroll:view" },
      { key: "/finance/close", label: "Daily Close", permission: "finance:close" },
      { key: "/finance/banking", label: "Banking & Cash Control" },
      { key: "/finance/settings", label: "Finance Setup", permission: "finance:accounts" },
    ],
  },
  {
    key: "/reports",
    icon: <BarChartOutlined />,
    label: "Reports & Analytics",
    permission: "reports:view",
  },
  {
    key: "/settings",
    icon: <SettingOutlined />,
    label: "Settings",
    permission: "settings:view",
  },
  {
    key: "/audit",
    icon: <AuditOutlined />,
    label: "Audit Log",
    permission: "settings:view",
    // Backend auditRoutes require the admin role (manager gets 403).
    roles: ["admin"],
  },
  {
    key: "/settings/printers",
    icon: <PrinterOutlined />,
    label: "Printers",
    permission: "settings:view",
  },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  // Bumped when the cached identity is refreshed from /auth/me.
  const [identityVersion, setIdentityVersion] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const currentUser = getDisplayUser();
  const userPermissions: string[] = currentUser.permissions || [];

  // Refresh the cached identity (permissions/status/role) from the server so
  // permission changes made by an admin take effect without a re-login.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1"}/auth/me`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` } },
        );
        const body = await res.json();
        if (cancelled || !res.ok || !body?.success || !body?.data) return;
        const cached = getStoredUser() || {};
        localStorage.setItem(
          "admin_user",
          JSON.stringify({ ...cached, ...body.data, permissions: body.data.permissions || [] }),
        );
        // Re-render with the refreshed permission set.
        setIdentityVersion((v) => v + 1);
      } catch {
        // Offline / transient failure — keep the cached copy.
      }
    };
    void refresh();
    return () => { cancelled = true; };
  }, []);

  // menuItems depends on `identityVersion` only to force re-filtering after a
  // permissions refresh; eslint-disable not needed as it is read below.
  void identityVersion;

  // Filter menu to only items the user has permission to see.
  // `roles` (if present) is an additional hard constraint — e.g. the backend
  // audit routes are admin-only regardless of the stored permission set.
  const userRole = currentUser.role;
  const canSee = (item: { permission?: string; roles?: string[] }) =>
    (!item.permission || userPermissions.includes(item.permission)) &&
    (!item.roles || (userRole ? item.roles.includes(userRole) : false));

  const menuItems = ALL_MENU_ITEMS.filter(canSee).map(({ permission, roles, ...rest }) => {
    if (!rest.children) return rest;
    // Filter children by their own permission (matches each route's PermissionRoute guard)
    return {
      ...rest,
      children: (rest.children as any[])
        .filter((child) => canSee(child))
        .map(({ permission: childPermission, roles: childRoles, ...childRest }) => childRest),
    };
  });

  useEffect(() => {
    const socket = socketService.connect();

    const handleNewBooking = (booking: any) => {
      setNotifications((prev) => [
        {
          id: Date.now().toString(),
          text: `New booking: ${booking.guest_name} (${booking.confirmation_number || "Pending"})`,
          type: "booking",
          read: false,
        },
        ...prev,
      ]);
    };

    const handleSystemNotification = (event: any) => {
      const createdAt = event?.createdAt
        ? dayjs(event.createdAt).format("h:mm A")
        : "";
      const title = event?.title || "System notification";
      const text = createdAt ? `${title} (${createdAt})` : title;

      setNotifications((prev) => [
        {
          id: event?.id || Date.now().toString(),
          text,
          type: event?.eventType || "system",
          read: false,
        },
        ...prev,
      ]);
    };

    socket.on("booking_created", handleNewBooking);
    socket.on("notification_created", handleSystemNotification);
    return () => {
      socket.off("booking_created", handleNewBooking);
      socket.off("notification_created", handleSystemNotification);
    };
  }, []);

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const notificationContent = (
    <div style={{ width: 320, maxHeight: 400, overflow: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "8px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Text strong>Notifications</Text>
        <Button type="link" size="small" onClick={markAllAsRead}>
          Mark all read
        </Button>
      </div>
      {notifications.length === 0 ? (
        <div style={{ padding: "16px", textAlign: "center" }}>
          No notifications
        </div>
      ) : (
        <List
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: "12px 16px",
                background: item.read ? "#fff" : "#e6f4ff",
                cursor: "pointer",
              }}
            >
              <Text style={{ fontWeight: item.read ? "normal" : "500" }}>
                {item.text}
              </Text>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const userMenuItems = [
    { key: "profile", icon: <UserOutlined />, label: "Profile" },
    { type: "divider" as const },
    { key: "logout", icon: <LogoutOutlined />, label: "Logout" },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === "profile") {
      navigate("/settings");
      return;
    }

    if (key === "logout") {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_refresh_token");
      localStorage.removeItem("admin_user");
      navigate("/login");
    }
  };

  const getCurrentPageTitle = () => {
    const path = location.pathname;
    const flat = ALL_MENU_ITEMS.flatMap((item) =>
      item.children
        ? item.children.map((c) => ({
            ...c,
            label: item.label + " / " + c.label,
          }))
        : [item],
    );
    return (
      flat.find((i) => i.key === path)?.label || "Adam's Palm Resort Admin"
    );
  };

  const sidebarContent = (
    <>
      <div className="admin-logo">
        <img
          src="/icon.png"
          alt="Adam's Palm Resort"
          style={{
            width: collapsed && !isMobile ? 40 : 60,
            height: collapsed && !isMobile ? 40 : 60,
            transition: "all 0.2s",
          }}
        />
        {(!collapsed || isMobile) && (
          <div>
            <Text
              strong
              style={{
                fontSize: "18px",
                marginBottom: "0px",
                color: "#b79a68",
              }}
            >
              Adam's Palm
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Resort Admin
            </Text>
          </div>
        )}
      </div>

      <Menu
        mode="inline"
        selectedKeys={[location.pathname + location.search]}
        style={{ borderRight: 0 }}
        items={menuItems}
        onClick={({ key }) => {
          handleMenuClick({ key });
          if (isMobile) setCollapsed(true);
        }}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isMobile ? (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setCollapsed(true)}
          open={!collapsed}
          styles={{ body: { padding: 0 } }}
          width={280}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          width={280}
          style={{
            overflow: "auto",
            height: "100vh",
            position: "fixed",
            left: 0,
            top: 0,
            bottom: 0,
            boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
            zIndex: 1000,
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 280,
          transition: "all 0.2s",
        }}
      >
        <Header
          style={{
            padding: "0 24px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 999,
          }}
        >
          <Space align="center">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: "16px", width: 48, height: 48 }}
            />
            <Text strong style={{ fontSize: "18px", marginLeft: 16 }}>
              {getCurrentPageTitle()}
            </Text>
          </Space>

          <Space align="center" size="large">
            <Popover
              content={notificationContent}
              trigger="click"
              placement="bottomRight"
              arrow={false}
              overlayInnerStyle={{ padding: 0 }}
            >
              <Badge
                count={notifications.filter((n) => !n.read).length}
                size="small"
              >
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  style={{ fontSize: "16px" }}
                />
              </Badge>
            </Popover>

            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <Space
                align="center"
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Avatar style={{ backgroundColor: "#b79a68" }}>
                  {currentUser.name?.[0]?.toUpperCase() || "A"}
                </Avatar>
                <div style={{ textAlign: "left", lineHeight: 1.2 }}>
                  <Text strong>{currentUser.name}</Text>
                  <br />
                  <Text
                    type="secondary"
                    style={{ fontSize: "12px", textTransform: "capitalize" }}
                  >
                    {currentUser.role}
                  </Text>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: "24px",
            padding: "24px",
            background: "#f5f5f5",
            minHeight: "calc(100vh - 112px)",
            borderRadius: "8px",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
